import { useState, useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import { ADOBE_FONTS } from "containers/admin/clients/client/theme-page/preview/preview.constants";

export const useAdobeFonts = () => {
  const {
    fonts,
    setFonts,
    typography,
    setTypography,
    adobeFontsList,
    isEditMode,
    adobeFontsFetchError,
  } = useThemePreviewContext();
  const { setValue, setError, clearErrors } = useFormContext();

  const adobeFontId = fonts?.[ADOBE_FONTS.key];

  const [isAdobeFontsRemovalPending, setIsAdobeFontsRemovalPending] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(!!adobeFontId);

  // Set form error if there's an error fetching Adobe fonts
  useEffect(() => {
    if (adobeFontsFetchError) {
      setError(ADOBE_FONTS.name, {
        type: "custom",
        message: adobeFontsFetchError,
      });
    }
  }, [adobeFontsFetchError, setError]);

  // Reset pending delete state when font ID is cleared from context
  useEffect(() => {
    if (!adobeFontId) {
      setIsAdobeFontsRemovalPending(false);
    }
  }, [adobeFontId]);

  // reset pending deletion state when edit mode is disabled
  useEffect(() => {
    if (!isEditMode) {
      setIsAdobeFontsRemovalPending(false);
    }
  }, [isEditMode]);

  // Helper to update form and context state
  const setAdobeFontsProjectId = useCallback(
    (projectId: string): void => {
      setFonts((prev) => ({
        [ADOBE_FONTS.key]: projectId,
        googleFonts: prev?.googleFonts || [],
      }));
      setValue(ADOBE_FONTS.name, projectId, { shouldDirty: true });
    },
    [setFonts, setValue],
  );

  // reset typography sections using deleted adobe fonts
  const resetTypographyWithAdobeFonts = useCallback((): void => {
    if (!typography || !adobeFontsList || adobeFontsList.length === 0) return;

    // create a map of Adobe font slugs
    const adobeFontSlugs = new Set(
      adobeFontsList
        .filter((font) => typeof font.slug === "string" && font.slug !== "")
        .map((font) => font.slug),
    );

    // check all typography sections
    const updatedTypography = { ...typography };
    Object.keys(typography).forEach((sectionName) => {
      const section = typography[sectionName];

      // reset font family if it matches an Adobe font
      if (section.fontFamily.value && adobeFontSlugs.has(section.fontFamily.value)) {
        updatedTypography[sectionName] = {
          ...section,
          fontFamily: { label: "Select...", value: "" },
        };

        setValue(`typography.${sectionName}.fontFamily`, { label: "Select...", value: "" });
      }
    });

    setTypography(updatedTypography);
  }, [typography, adobeFontsList, setTypography, setValue]);

  // Remove Adobe Fonts stylesheet
  const removeStylesheet = useCallback((projectId: string): void => {
    const stylesheet = document.querySelector(
      `link[href="https://use.typekit.net/${projectId}.css"]`,
    );
    if (stylesheet) {
      stylesheet.remove();
    }
  }, []);

  // Helper to clear project ID from form and context
  const clearAdobeFontsProjectId = useCallback((): void => {
    // Remove the Adobe Fonts stylesheet if there's a project ID
    if (adobeFontId) {
      removeStylesheet(adobeFontId);
    }

    // Reset typography fields that were using Adobe Fonts first
    resetTypographyWithAdobeFonts();

    // Then clear the project ID and other state
    setAdobeFontsProjectId("");
    setIsAdobeFontsRemovalPending(false);
    clearErrors(ADOBE_FONTS.name);
  }, [
    setAdobeFontsProjectId,
    clearErrors,
    resetTypographyWithAdobeFonts,
    adobeFontId,
    removeStylesheet,
  ]);

  // Load Adobe Fonts stylesheet
  const appendStylesheet = useCallback((projectId: string): Promise<void> => {
    const link = document.createElement("link");
    link.href = `https://use.typekit.net/${projectId}.css`;
    link.rel = "stylesheet";

    return new Promise((resolve, reject) => {
      link.onload = () => resolve();
      link.onerror = () => reject(new Error("Please fill in valid Project ID"));
      document.head.appendChild(link);
    });
  }, []);

  // Main function to handle project ID updates
  const handleSubmitAdobeFonts = useCallback(
    async (projectId: string) => {
      setHasSubmitted(true);

      if (isAdobeFontsRemovalPending) {
        clearAdobeFontsProjectId();
        return;
      }

      if (!projectId) return;

      const trimmedProjectId = projectId.trim();
      if (!trimmedProjectId) return;

      try {
        // Try to load the stylesheet first to validate the project ID
        await appendStylesheet(trimmedProjectId);
        // If successful, update the project ID
        setAdobeFontsProjectId(trimmedProjectId);
        clearErrors(ADOBE_FONTS.name);
      } catch (err) {
        // If there's an error, set it immediately
        setError(ADOBE_FONTS.name, {
          type: "custom",
          message: "Invalid Project ID",
        });
        // Don't update the project ID if validation fails
        return;
      }
    },
    [
      isAdobeFontsRemovalPending,
      clearAdobeFontsProjectId,
      setAdobeFontsProjectId,
      appendStylesheet,
      setError,
      clearErrors,
    ],
  );

  // Handle clearing the input
  const handleClearAdobeFontsInput = useCallback(
    (value: string, isErrOccured: boolean) => {
      if (value === adobeFontId && !isErrOccured) {
        // If clearing a saved value and there is no err, mark for deletion
        setIsAdobeFontsRemovalPending(true);
      } else {
        // If clearing an unsaved value or error, reset everything
        setValue(ADOBE_FONTS.name, "");
        clearErrors(ADOBE_FONTS.name);
        setAdobeFontsProjectId("");
        setIsAdobeFontsRemovalPending(false);
        setHasSubmitted(false);
      }
    },
    [adobeFontId, setValue, clearErrors, setAdobeFontsProjectId],
  );

  // Handle undoing a pending deletion
  const handleUndoClearAdobeFontsInput = useCallback(() => {
    setIsAdobeFontsRemovalPending(false);
    setValue(ADOBE_FONTS.name, adobeFontId || "");
  }, [adobeFontId, setValue]);

  // Determine if submit button should be active
  const isAdobeFontsActionAvailable = useCallback(
    (fieldValue: string, hasError: boolean) => {
      if (hasError) return false;
      return isAdobeFontsRemovalPending || (fieldValue !== adobeFontId && fieldValue !== "");
    },
    [isAdobeFontsRemovalPending, adobeFontId],
  );

  return {
    isAdobeFontsRemovalPending,
    hasSubmitted,
    setHasSubmitted,
    handleClearAdobeFontsInput,
    handleUndoClearAdobeFontsInput,
    handleSubmitAdobeFonts,
    isAdobeFontsActionAvailable,
    resetTypographyWithAdobeFonts,
    appendStylesheet,
  };
};
